import * as React from 'react';
import { StarRouter, ToType } from 'src/domains/layouts/state/router/StarRouter';
import { observer } from 'src/utils/mobx-react';
import { NewLinkWrapper } from './NewLink.style';
import { assertNever } from 'src_common/common/assertNever';
import { useCommon } from 'src/domains/common/Common';

const getUrl = (starRouter: StarRouter, to: ToType | undefined): string => {
    if (to === undefined) {
        return '';
    }

    if (typeof to === 'string') {
        return to;
    }

    return starRouter.buildUrlTo(to);
};

export type LinkPropsType = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    //TODO - It would be good to remove the undefined from the props "to"
    to?: ToType;
    className?: string;
    size?: 'large' | 'medium' | 'small' | 'xs';
    isButton?: boolean;
    onClick?: () => void;
    dataTest?: string;
};

interface LinkAttrType {
    target: string | undefined;
    rel: string | undefined;
}

const getLinkAttr = (starRouter: StarRouter, href: string): LinkAttrType => {
    const result = starRouter.isLinkExternal(href);

    if (result.type === 'parseError') {
        return {
            target: undefined,
            rel: undefined,
        };
    }

    if (result.type === 'onserver') {
        return {
            target: undefined,
            rel: undefined,
        };
    }

    if (result.type === 'external') {
        return {
            target: '_blank',
            rel: 'noopener',
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (result.type === 'internal') {
        return {
            target: '_self',
            rel: undefined,
        };
    }

    return assertNever('getLinkAttr', result);
};

export const Link = observer('NewLink', ({ to, ...props }: LinkPropsType) => {
    const { children, className, isButton, size, dataTest, onClick } = props;
    const common = useCommon();
    const starRouter = StarRouter.get(common);
    const href = getUrl(starRouter, to);
    const linkAttr = getLinkAttr(starRouter, href);

    const redirect = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();

        if (onClick !== undefined) {
            onClick();
        }

        starRouter.handleUrlRedirection(href);
    };

    return (
        <NewLinkWrapper
            {...props}
            isButton={isButton}
            className={className}
            size={size}
            href={href}
            data-test={dataTest === undefined ? null : `${dataTest}-link`}
            onClick={redirect}
            target={linkAttr.target}
            rel={linkAttr.rel}
        >
            {children}
        </NewLinkWrapper>
    );
});
